import React, { useState, useEffect } from "react";
import { Button, Grid, Container, TextField } from "@mui/material";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import {
  CustomTitle,
  CustomValue,
} from "../../components/Custom/CustomTypography";
import CustomButton from "../../components/Custom/CustomButton";
import { useSearchParams } from "react-router-dom";
import { useAlert } from "../../components/Alert";
import { useNavigate } from "react-router-dom";
import { compressImage } from "../../globals/utils";

export default function CreatePesticideFertilizer() {
  const [searchParams] = useSearchParams();
  const [crops, setCrops] = useState([]);
  const [formData, setFormData] = useState({});
  const [limits, setLimits] = useState({});
  const [selectedFileName, setSelectedFileName] = useState("");
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const fertilizerId = searchParams.get("fertilizerId");
  const cropId = searchParams.get("cropId");
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (fertilizerId) {
      fetchFertilizer(fertilizerId);
      setIsEdit(true);
    }
  }, [isEdit, fertilizerId]);

  const fetchFertilizer = (fertilizerId) => {
    api
      .get(`/farmer/pesticide_fertilizer/${fertilizerId}`)
      .then((response) => {
        if (response && response.data) {
          const fertilizerData = response.data;
          console.log(fertilizerData);
          setFormData({
            crop_id: fertilizerData.crop.crop_type,
            application_type: fertilizerData.application_type,
            product_brand_name: fertilizerData.product_brand_name,
            method: fertilizerData.method,
            quantity: fertilizerData.quantity,
            area: fertilizerData.area,
            application_date: fertilizerData.application_date,
            source: fertilizerData.source,
            application_file: fertilizerData.application_file,
          });
        }
      })
      .catch((error) => {
        console.log("Error fetching fertilizer data:", error);
      });
  };

  useEffect(() => {
    api.get("/farmer/crops/").then((res) => {
      const _crops = res.data.map((f) => {
        const name = `${f.farmland?.farm_name} ${f.commodity?.name} [${f.sowing_date}]`;
        return { id: f.id, crop_name: name, farmland: f.farmland };
      });
      setCrops(_crops);
      if (cropId) {
        const selectedCrop = _crops.find(
          (crop) => crop.id === parseInt(cropId, 10)
        );
        if (selectedCrop) {
          setFormData((prevData) => ({
            ...prevData,
            crop_id: selectedCrop.id,
          }));
        }
      }
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const compressedFile = await compressImage(file);
      setFormData((prevData) => ({
        ...prevData,
        application_file: compressedFile,
      }));
    } catch (error) {
      console.error("Error compressing image:", error);
      showAlert(
        "error",
        "An error occurred while compressing the image. Please try again."
      );
    }
    setSelectedFileName(file.name);
  };

  const createCrop = () => {
    const fileFields = ["application_file"];

    const _formData = new FormData();
    for (const key in formData) {
      if (key === "land_doc" && formData[key] === "") {
        continue;
      }
      if (formData[key] instanceof FileList) {
        _formData.append(key, formData[key][0]);
      } else {
        if (fileFields.includes(key) && typeof formData[key] === "string")
          continue;
        _formData.append(key, formData[key]);
      }
    }

    const apiRequest = isEdit
      ? api.patch(`/farmer/pesticide_fertilizer/${fertilizerId}/`, _formData)
      : api.post("/farmer/pesticide_fertilizer/", _formData);

    apiRequest
      .then((response) => {
        console.log("Response data:", response.data);
        showAlert(
          "Success",
          isEdit
            ? "Update Successfully"
            : "Input Supply Data added successfully"
        );
        const redirectUrl = cropId ? `/farmer/crop/${cropId}` : "/";
        navigate(redirectUrl);
      })
      .catch((error) => {
        console.log(error);
        showAlert("Some error occured");
      });
  };

  useEffect(() => {
    const cropData = crops.find((c) => c.id === formData.crop_id);
    console.log(cropData);
    // Set area from cropData
    if (cropData) {
      setFormData((prevData) => ({
        ...prevData,
        area: cropData.farmland?.area,
      }));
      setLimits(() => ({
        ...limits,
        maxArea: cropData.farmland?.area,
      }));
    }
  }, [formData.crop_id]);

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xs">
        <div>
          <Grid
            container
            // spacing={1}
            justify="center"
            direction="column"
            style={{ marginTop: "10px" }}
          >
            <CustomTitle>
              {isEdit ? t("Edit Input Supply") : t("Add Input Supply")}
            </CustomTitle>

            <FormControl fullWidth sx={{ marginTop: 2 }}>
              <InputLabel id="token-select-label">{t("Crop")}</InputLabel>
              <Select
                label={t("Crop")}
                name="crop_id"
                value={formData.crop_id || ""}
                onChange={handleChange}
                disabled={!!cropId} // Disable if cropId is present
              >
                {crops.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.crop_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ marginTop: 2 }}>
              <InputLabel id="crop-select-label">
                {t("Application Type")}
              </InputLabel>
              <Select
                label={t("Application Type")}
                name="application_type"
                value={formData.application_type || ""}
                onChange={handleChange}
              >
                <MenuItem value={"fertilizer"}>{t("Fertilizer")}</MenuItem>
                <MenuItem value={"manure"}>{t("Manure")}</MenuItem>
                <MenuItem value={"pesticide"}>{t("Pesticide")}</MenuItem>
                <MenuItem value={"fungicide"}>{t("Fungicide")}</MenuItem>
                <MenuItem value={"weedicide"}>{t("Weedicide")}</MenuItem>
                <MenuItem value={"other"}>{t("Other")}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="normal"
              required
              fullWidth
              label={t("Product Brand Name")}
              type="text"
              name="product_brand_name"
              value={formData.product_brand_name || ""}
              onChange={handleChange}
            />

            <FormControl fullWidth sx={{ marginTop: 2 }}>
              <InputLabel id="method-select-label">{t("Method")}</InputLabel>
              <Select
                label={t("Method")}
                name="method"
                value={formData.method || ""}
                onChange={handleChange}
              >
                <MenuItem value={"sprayed"}>{t("Sprayed")}</MenuItem>
                <MenuItem value={"thrown"}>{t("Thrown")}</MenuItem>
                <MenuItem value={"drip_irrigation"}>
                  {t("Through Drip Irrigation")}
                </MenuItem>
                <MenuItem value={"other"}>{t("Other")}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="normal"
              required
              fullWidth
              label={t("Quantity(Kg/L)")}
              type="number"
              name="quantity"
              value={formData.quantity || ""}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label={t("Applied Area(acres)")}
              type="number"
              name="area"
              value={formData.area || ""}
              onChange={handleChange}
              error={formData.area > limits.maxArea}
              helperText={
                formData.area > limits.maxArea
                  ? t("Area cannot be more than farm area")
                  : ""
              }
            />
            <TextField
              margin="normal"
              required
              fullWidth
              type="date"
              label={t("Application Date")}
              name="application_date"
              value={formData.application_date || ""}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label={t("Source")}
              name="source"
              value={formData.source || ""}
              onChange={handleChange}
            />

            <Grid container sx={{ marginTop: "10px" }}>
              <Grid item xs={8}>
                <Typography style={{ fontSize: "12px", fontWeight: 600 }}>
                  {t("Upload File")}
                </Typography>
                <span
                  style={{
                    marginTop: "-20px",
                    fontSize: "10px",
                    fontWeight: 400,
                  }}
                >
                  {t("( optional)")}
                </span>
              </Grid>

              <Grid item xs={4}>
                <Button
                  variant="contained"
                  component="label"
                  style={{
                    marginLeft: "auto",
                    color: "white",
                    width: "100%",
                    height: "33px",
                  }}
                  htmlFor="application-file"
                >
                  {t("Upload")}
                </Button>
                <input
                  type="file"
                  id="application-file"
                  name="application_file"
                  onChange={onFileChange}
                  style={{ display: "none" }}
                />
              </Grid>
            </Grid>
            {selectedFileName && <CustomValue> {selectedFileName}</CustomValue>}
            <CustomButton onClick={createCrop}>
              {isEdit ? t("Edit Input") : t("Add Input")}
            </CustomButton>
          </Grid>
        </div>
      </Container>
    </>
  );
}
